import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

const NONE = "__none";
export const VIA = { lesson: "this lesson", lab: "lab default", teacher: "teacher default" };

export const assignTech = async (body, label) => {
  try {
    const r = await api.post("/timetable/assign-technician", body);
    toast.success(`${label}${r.data.backfilled ? ` · ${r.data.backfilled} open request${r.data.backfilled > 1 ? "s" : ""} assigned` : ""}`);
    return true;
  } catch (e) {
    toast.error(errMsg(e));
    return false;
  }
};

export function TechSelect({ techs, value, onChange, testId, placeholder = "Choose technician" }) {
  return (
    <Select value={value || NONE} onValueChange={(v) => onChange(v === NONE ? null : v)}>
      <SelectTrigger className="w-64 max-w-full" data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        <SelectItem value={NONE}>No technician</SelectItem>
        {techs.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export function TechBulkBar({ selected, techs, onApplied, onCancel }) {
  const [tid, setTid] = useState(null);
  const apply = async (techId) => {
    const name = techs.find((t) => t.id === techId)?.name;
    if (await assignTech({ entry_ids: selected, technician_id: techId }, techId ? `${selected.length} lesson(s) assigned to ${name}` : `Technician removed from ${selected.length} lesson(s)`)) onApplied();
  };
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border border-primary/30 bg-primary/5 p-3 mb-4" data-testid="tech-bulk-bar">
      <span className="text-sm font-semibold mr-2" data-testid="tech-bulk-count">{selected.length} lesson{selected.length === 1 ? "" : "s"} selected</span>
      <TechSelect techs={techs} value={tid} onChange={setTid} testId="tech-bulk-select" />
      <Button size="sm" disabled={!selected.length || !tid} onClick={() => apply(tid)} data-testid="tech-bulk-apply">Assign</Button>
      <Button size="sm" variant="outline" disabled={!selected.length} onClick={() => apply(null)} data-testid="tech-bulk-clear">Remove technician</Button>
      <Button size="sm" variant="ghost" className="ml-auto" onClick={onCancel} data-testid="tech-bulk-cancel">Done</Button>
    </div>
  );
}

function DefaultRow({ label, value, techs, onChange, testId }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b last:border-0">
      <span className="text-sm">{label}</span>
      <TechSelect techs={techs} value={value} onChange={onChange} testId={testId} placeholder="No technician" />
    </div>
  );
}

export function TechDefaultsDialog({ open, onOpenChange, onChanged }) {
  const [setup, setSetup] = useState(null);
  const load = () => api.get("/timetable/tech-setup").then((r) => setSetup(r.data));
  useEffect(() => {
    if (open) {
      load();
    }
  }, [open]);
  const change = async (body, label) => {
    if (await assignTech(body, label)) {
      load();
      onChanged();
    }
  };
  const nameOf = (id) => setup.technicians.find((t) => t.id === id)?.name;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="tech-defaults-dialog">
        <DialogHeader>
          <DialogTitle>Technician defaults</DialogTitle>
          <DialogDescription>Requests go to the technician set on the lesson first, then the lab, then the teacher. With none set, every technician is told.</DialogDescription>
        </DialogHeader>
        {!setup ? <p className="text-sm text-muted-foreground">Loading…</p> : (
          <div className="space-y-5">
            <section><h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-1">By lab</h3>
              {setup.labs.map((l) => <DefaultRow key={l.id} label={l.name} value={l.technician_id} techs={setup.technicians} testId={`tech-default-lab-${l.id}`}
                onChange={(v) => change({ lab_id: l.id, technician_id: v }, v ? `${l.name} → ${nameOf(v)}` : `${l.name} cleared`)} />)}
            </section>
            <section><h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-1">By teacher</h3>
              {setup.teachers.map((t) => <DefaultRow key={t.id} label={t.name} value={t.default_technician_id} techs={setup.technicians} testId={`tech-default-teacher-${t.id}`}
                onChange={(v) => change({ teacher_id: t.id, technician_id: v }, v ? `${t.name}'s lessons → ${nameOf(v)}` : `${t.name} cleared`)} />)}
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
